

export const VAT_RATE = 0.16;
export const POSITIONING_FEE = 7000;

export const GLOBAL_VILLAGE_EVENT_KEY = "66489b2f-1fa8-4307-a57d-d62a60dba217";

// ── Global Village — locked, do not edit ──
const GLOBAL_VILLAGE_RULESET = {
  TENT_PRICES: {
    "20x20_single": 60000,
    "20x20_shared": 40000,
    "10x10_single": 45000,
    food_court:     75000,
    pod_shared:     20000,
    "20x20_open":   60000,
    sponsored:      0,
    WFTO:           0,
    ITC:            0,
  },
  EXTRA_PRICES: {
    "Hay Bales":     431.03,
    "Wristbands":    258.62,
    "Single Socket": 2500,
  },
  // ITC didn't exist as a distinct type for Global Village vendors — only
  // sponsored supported a custom price at the time they applied.
  CUSTOM_PRICE_TENT_TYPES: new Set(["sponsored"]),
  // Only WFTO waived Single Socket for Global Village.
  WAIVED_SINGLE_SOCKET_TENT_TYPES: new Set(["WFTO"]),
};

// ── Default ruleset — Christmas 2026 onward ──
const DEFAULT_RULESET = {
  TENT_PRICES: {
    "20x20_single": 90000,
    "20x20_shared": 55000,
    "10x10_single": 60000,
    food_court:     95000,
    pod_shared:     30000,
    "20x20_open":   90000,
    sponsored:      0,
    WFTO:           0,
    ITC:            0,
    "biz_boost": 7500,
  },
  EXTRA_PRICES: {
    "Hay Bales":     431.03,
    "Wristbands":    258.62,
    "Single Socket": 3000,
  },
  CUSTOM_PRICE_TENT_TYPES: new Set(["sponsored", "ITC"]),
  WAIVED_SINGLE_SOCKET_TENT_TYPES: new Set(["WFTO", "food_court", "20x20_single"]),
};

const RULESETS_BY_EVENT_KEY = {
  [GLOBAL_VILLAGE_EVENT_KEY]: GLOBAL_VILLAGE_RULESET,
};

/**
 * Returns the pricing ruleset that applies to a given event. Falls back to
 * DEFAULT_RULESET for any event not explicitly pinned above.
 */
export function getRuleset(eventKey) {
  return RULESETS_BY_EVENT_KEY[eventKey] || DEFAULT_RULESET;
}

// Backward-compatible flat exports — DEFAULT_RULESET only. Any code still
// importing these directly (instead of going through getRuleset/
// computeInvoice/isExtraWaived with an eventKey) is NOT event-aware and
// will silently apply Christmas+ pricing regardless of which event it's
// actually working with. Prefer the eventKey-aware functions below for any
// new or updated call site.
export const TENT_PRICES = DEFAULT_RULESET.TENT_PRICES;
export const EXTRA_PRICES = DEFAULT_RULESET.EXTRA_PRICES;
export const CUSTOM_PRICE_TENT_TYPES = DEFAULT_RULESET.CUSTOM_PRICE_TENT_TYPES;
export const WAIVED_SINGLE_SOCKET_TENT_TYPES = DEFAULT_RULESET.WAIVED_SINGLE_SOCKET_TENT_TYPES;

/**
 * Whether a given extra is waived for a given tent type, under the
 * pricing rules for the given event.
 */
export function isExtraWaived(extraName, tentType, eventKey) {
  const ruleset = getRuleset(eventKey);
  return extraName === "Single Socket" && ruleset.WAIVED_SINGLE_SOCKET_TENT_TYPES.has(tentType);
}

/**
 * Computes the full invoice breakdown for a vendor application, under the
 * pricing rules for the given event.
 *
 * @param {object} params
 * @param {string} params.tentType - app.tent_type
 * @param {number|string|null} [params.customBasePrice] - app.custom_base_price, used only when tentType supports a custom price for this event's ruleset
 * @param {object|null} [params.extras] - app.extras, keyed by extra name → qty
 * @param {boolean} params.hasChosenSpot - whether the positioning fee applies (app.chosen_spot is set)
 * @param {string} params.eventKey - event.event_key || event.id — selects which pricing ruleset applies
 * @returns {{ basePrice: number, positioningFee: number, extrasTotal: number, subtotal: number, vatAmount: number, total: number, depositRequired: number }}
 */
export function computeInvoice({ tentType, customBasePrice, extras, hasChosenSpot, eventKey }) {
  const ruleset = getRuleset(eventKey);
  const supportsCustomPrice = ruleset.CUSTOM_PRICE_TENT_TYPES.has(tentType);
  const basePrice = supportsCustomPrice ? Number(customBasePrice || 0) : (ruleset.TENT_PRICES[tentType] ?? 0);
  const positioningFee = hasChosenSpot ? POSITIONING_FEE : 0;

  const extrasTotal = Object.entries(extras || {}).reduce(
    (t, [key, qty]) =>
      t + (isExtraWaived(key, tentType, eventKey) ? 0 : (ruleset.EXTRA_PRICES[key] || 0)) * Number(qty || 0),
    0
  );

  const subtotal = basePrice + extrasTotal + positioningFee;
  const vatAmount = subtotal * VAT_RATE;
  const total = Math.round(subtotal + vatAmount);
  const depositRequired = Math.round(total * 0.5);

  return { basePrice, positioningFee, extrasTotal, subtotal, vatAmount, total, depositRequired };
}